// src/pages/MebliPage.jsx
import { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import AOS from 'aos';
import 'aos/dist/aos.css';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

import SeoMeta from '../components/SeoMeta';
import { usePageMeta } from '../hooks/usePageMeta';

// ---------- Styled ----------
const Page = styled.div``;

const Section = styled.section`
  padding: 72px 0;
  &.alt { background: ${({theme})=>theme.body}; }
`;

const Container = styled.div`
  width: ${({theme})=>theme.container};
  margin: 0 auto;
`;

const Hero = styled.section`
  padding: clamp(48px, 8vw, 104px) 0;
  background:
    radial-gradient(80% 100% at 50% 0%, rgba(255,102,0,.10) 0%, rgba(255,102,0,0) 60%),
    ${({theme})=> theme.mode==='light' ? '#fff' : '#0f1012'};
  border-bottom: 1px solid ${({theme})=>theme.border};
`;
const HeroInner = styled.div`
  width: ${({theme})=>theme.container}; margin: 0 auto;
  display: grid; gap: 24px;
  grid-template-columns: 1.2fr .8fr;
  align-items: center;
  @media (max-width: 980px){ grid-template-columns: 1fr; }
`;
const Badge = styled.span`
  display:inline-flex; gap:8px; align-items:center;
  padding:6px 12px; border-radius:${({theme})=>theme.radius.pill}px;
  border:1px solid ${({theme})=>theme.border};
  background:${({theme})=>theme.surface}; font-weight:700; font-size:14px;
  width: fit-content;
`;
const H1 = styled.h1`font-size:${({theme})=>theme.font.sizes.h1}; margin:8px 0 12px;`;
const Lead = styled.p`font-size:20px; color:${({theme})=>theme.textMuted};`;
const Actions = styled.div`display:flex; gap:10px; flex-wrap:wrap; margin-top: 8px;`;

const Button = styled.a`
  display:inline-flex; align-items:center; gap:10px; padding:12px 18px;
  border-radius:${({theme})=>theme.radius.pill}px;
  border:1px solid ${({theme})=>theme.border}; cursor:pointer; text-decoration:none;
  background:${({primary, theme})=> primary ? theme.accent : 'transparent'};
  color:${({primary})=> primary ? '#111' : 'inherit'};
  box-shadow:${({primary})=> primary ? '0 8px 24px rgba(255,102,0,.35)' : 'none'};
  font-weight:800;
  &:hover{ opacity:.9; }
`;

const HeroCard = styled.div`
  border:1px solid ${({theme})=>theme.border};
  background:${({theme})=>theme.surface};
  border-radius:${({theme})=>theme.radius.lg}px;
  box-shadow:${({theme})=>theme.shadow.sm};
  padding:18px;
  display:grid; gap:12px;
`;

const Grid = styled.div`
  display:grid; gap:16px;
  grid-template-columns: repeat(12, 1fr);
`;
const Card = styled.div`
  grid-column: span 4;
  @media (max-width: 980px){ grid-column: span 6; }
  @media (max-width: 640px){ grid-column: span 12; }

  border:1px solid ${({theme})=>theme.border};
  background:${({theme})=>theme.surface};
  border-radius:${({theme})=>theme.radius.lg}px;
  box-shadow:${({theme})=>theme.shadow.sm};
  overflow:hidden;

  .thumb{
    aspect-ratio: 16 / 10;
    background: linear-gradient(135deg, rgba(255,102,0,.22), rgba(0,0,0,.08));
    display:flex; align-items:center; justify-content:center;
    font-size:40px;
  }
  .body{ padding:16px; }
  h3{ margin:0 0 8px; font-size:18px; }
  p{ margin:0; color:${({theme})=>theme.textMuted}; font-size:15px; }
`;

const Pill = styled.span`
  display:inline-flex; padding:6px 10px; font-size:13px; font-weight:700;
  border:1px solid ${({theme})=>theme.border};
  border-radius:${({theme})=>theme.radius.pill}px; color:${({theme})=>theme.textMuted};
  background:${({theme})=>theme.surface};
`;

const Steps = styled.ol`
  display:grid; gap:16px; counter-reset: s;
  grid-template-columns: repeat(12, 1fr);
  li{
    grid-column: span 3;
    @media (max-width: 980px){ grid-column: span 6; }
    @media (max-width: 640px){ grid-column: span 12; }
    list-style:none; position:relative; padding:16px 16px 16px 52px;
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.surface};
    border-radius:${({theme})=>theme.radius.lg}px; box-shadow:${({theme})=>theme.shadow.sm};
  }
  li::before{
    counter-increment: s; content: counter(s);
    position:absolute; left:16px; top:16px; width:28px; height:28px; line-height:28px;
    text-align:center; border-radius:50%;
    background:${({theme})=>theme.accent}; color:#111; font-weight:900;
  }
`;

const Kbd = styled.kbd`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  padding:4px 8px; border-radius:${({theme})=>theme.radius.pill}px; border:1px solid ${({theme})=>theme.border};
  background:${({theme})=>theme.surface}; font-size:13px;
`;

const Row = styled.div`
  display:grid; gap:16px; grid-template-columns: 1fr 1fr;
  @media (max-width: 980px){ grid-template-columns: 1fr; }
`;

const List = styled.ul`
  margin: 0; padding: 0; display:grid; gap:8px;
  li{ list-style:none; display:flex; align-items:center; gap:10px; }
  li::before{ content:'•'; color:${({theme})=>theme.accent}; font-weight:900; }
`;

const CTA = styled.div`
  border:1px solid ${({theme})=>theme.border};
  background: linear-gradient(180deg, rgba(255,102,0,.12), transparent 60%), ${({theme})=>theme.surface};
  border-radius:${({theme})=>theme.radius.xl}px;
  box-shadow:${({theme})=>theme.shadow.md}; padding:24px;
  display:grid; gap:10px;
`;

// ---------- Page ----------
export default function MebliPage(){
  const meta = usePageMeta('mebli');
  useEffect(()=>{
    AOS.init({ duration: 700, once: true, offset: 80 });
  },[]);

  const services = useMemo(()=>[
    { icon:'🏢', title:'Меблі для офісів', text:'Робочі місця, переговорні, ресепшн, коворкінги. Ергономіка, кабель-менеджмент, акустика.' },
    { icon:'🏠', title:'Меблі для дому', text:'Кухні, шафи-купе, гардеробні, вітальні, дитячі. Встановлення та сервіс.' },
    { icon:'🛍️', title:'Торгівельне обладнання', text:'Острівці, вітрини, стелажі, POS-елементи для рітейлу та виставок.' },
    { icon:'🪑', title:'Індивідуальні вироби', text:'Під ваші креслення і ТЗ. Серійність від 1 шт до малих партій.' },
    { icon:'🧩', title:'HPL-фасади та стільниці', text:'Стійкі до вологи та стирання, велика палітра декорів.' },
    { icon:'🛠️', title:'Монтаж та логістика', text:'Власні бригади, пакування, доставка авто/залізницею по всій Україні.' },
  ],[]);

  const gallery = useMemo(()=>[
    { title:'Kitchen Pro / HPL + МДФ', tag:'Кухня', img:'' },
    { title:'Reception Line', tag:'Офіс', img:'' },
    { title:'Retail Stand X', tag:'Рітейл', img:'' },
    { title:'Wardrobe Wall', tag:'Дім', img:'' },
    { title:'Meeting Hub', tag:'Офіс', img:'' },
  ],[]);

  const finishes = [
    'HPL (ламінат високого тиску)',
    'МДФ фарбована / шпонована',
    'ЛДСП Egger/Kronospan',
    'Стільниці compact HPL / штучний камінь',
    'Кромка ABS / алюмінієвий профіль',
    'Металеві елементи: ніжки, каркаси, фурнітура'
  ];

  const steps = [
    'Бриф/виміри та концепція',
    '3D-візуалізація і підбір матеріалів',
    'Виготовлення на виробництві',
    'Доставка і монтаж на локації'
  ];

  const articles = [
    { id:'hpl-vs-mdf', title:'HPL vs MDF: що обрати для кухні', date:'2025-08-15' },
    { id:'office-ergonomics', title:'Ергономіка офісу: 7 правил робочого місця', date:'2025-07-20' },
    { id:'retail-zoning', title:'Зонування торгових площ — кейси MOMV', date:'2025-06-30' },
  ];

  return (
    <Page>
      <SeoMeta
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        image={meta.image}
        path={meta.path || '/mebli'}
      />

      {/* HERO */}
      <Hero>
        <HeroInner>
          <div data-aos="fade-right">
            <Badge>Меблеве виробництво MOMV</Badge>
            <H1>Офісні, домашні та&nbsp;торговельні меблі<br/>під ваш простір і задачі</H1>
            <Lead>Проєктуємо, виготовляємо та монтуємо. HPL-фасади, стільниці, металокаркаси, акустичні панелі. Серійність — від 1 шт до малих партій.</Lead>
            <Actions>
              <Button primary href="/contacts">Залишити запит</Button>
              <Button href="/articles">Читати статті</Button>
            </Actions>
            <div style={{marginTop:14}}>
              <Pill>Власне виробництво</Pill>&nbsp;&nbsp;
              <Pill>Гарантія до 24 міс</Pill>&nbsp;&nbsp;
              <Pill>Доставка по Україні</Pill>
            </div>
          </div>

          <HeroCard data-aos="zoom-in">
            <div style={{display:'grid', gap:8}}>
              <strong>Швидкий бриф</strong>
              <small style={{color:'var(--muted, rgba(0,0,0,.6))'}}>Відповісти на 5 питань — й ми запропонуємо рішення та бюджет.</small>
              <List>
                <li>Призначення меблів (офіс/дім/рітейл)</li>
                <li>Габарити / план приміщення</li>
                <li>Матеріали та колір</li>
                <li>Термін і локація монтажу</li>
              </List>
              <div style={{display:'flex', gap:10, marginTop:12}}>
                <Button primary href="/contacts#brief">Заповнити бриф</Button>
                <Button href="tel:+380993682320">Зателефонувати</Button>
              </div>
            </div>
          </HeroCard>
        </HeroInner>
      </Hero>

      {/* SERVICES */}
      <Section className="alt">
        <Container>
          <h2 data-aos="fade-up">Наші послуги</h2>
          <p data-aos="fade-up" data-aos-delay="60">Повний цикл: від замірів та 3D-візуалізації до монтажу на об’єкті.</p>
          <Grid style={{marginTop:16}}>
            {services.map((s, i)=>(
              <Card key={i} data-aos="fade-up" data-aos-delay={i*60}>
                <div className="thumb">{s.icon}</div>
                <div className="body">
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                </div>
              </Card>
            ))}
          </Grid>
        </Container>
      </Section>

      {/* GALLERY / WORKS */}
      <Section>
        <Container>
          <h2 data-aos="fade-up">Обрані роботи</h2>
          <p data-aos="fade-up" data-aos-delay="60">Кухні, офісні зони, ресепшн, стенди для виставок та рітейлу.</p>

          <div data-aos="zoom-in" style={{marginTop: 12}}>
            <Swiper
              modules={[Autoplay, Pagination]}
              autoplay={{ delay: 2800, disableOnInteraction:false }}
              pagination={{ clickable: true }}
              loop
              spaceBetween={14}
              slidesPerView={1.1}
              breakpoints={{
                640: { slidesPerView: 2 },
                980: { slidesPerView: 3 }
              }}
            >
              {gallery.map((g, i)=>(
                <SwiperSlide key={i}>
                  <Card as="article" style={{height:'100%'}}>
                    <div className="thumb">
                      {/* якщо немає фото — емоджі */}
                      <span aria-hidden>🖼️</span>
                    </div>
                    <div className="body">
                      <Pill>{g.tag}</Pill>
                      <h3 style={{marginTop:8}}>{g.title}</h3>
                      <p>Індивідуальне рішення з урахуванням планування та матеріалів HPL/МДФ.</p>
                    </div>
                  </Card>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </Container>
      </Section>

      {/* PROCESS */}
      <Section className="alt">
        <Container>
          <Row>
            <div data-aos="fade-right">
              <h2>Як ми працюємо</h2>
              <p>Прозорий процес і прогнозовані терміни виготовлення.</p>
              <Steps style={{marginTop:12}}>
                {steps.map((t, i)=>(
                  <li key={i}>
                    <strong>{t}</strong>
                    <p style={{marginTop:6}}>
                      {i===0 && 'Приїдемо на об’єкт або працюємо за кресленнями/планом.'}
                      {i===1 && 'Підберемо декори, фурнітуру та погодимо кошторис.'}
                      {i===2 && 'Власний парк обладнання: форматка, кромка, фрезер, фарбувальна.'}
                      {i===3 && 'Обережне пакування, доставка, чистий монтаж та прибирання.'}
                    </p>
                  </li>
                ))}
              </Steps>
            </div>
            <div data-aos="fade-left">
              <h2>Матеріали та оздоблення</h2>
              <p>Добираємо під бюджет, навантаження та умови експлуатації.</p>
              <List style={{marginTop:12}}>
                {finishes.map((f,i)=><li key={i}>{f}</li>)}
              </List>
              <div style={{marginTop:14, display:'flex', gap:10, flexWrap:'wrap'}}>
                <Kbd>Egger</Kbd>
                <Kbd>Kronospan</Kbd>
                <Kbd>Blum</Kbd>
                <Kbd>Hettich</Kbd>
                <Kbd>Arpa / Fundermax</Kbd>
              </div>
            </div>
          </Row>
        </Container>
      </Section>

      {/* PRODUCTS / CATALOG PREVIEW */}
      <Section>
        <Container>
          <h2 data-aos="fade-up">Популярні категорії</h2>
          <Grid style={{marginTop:16}}>
            {[
              {icon:'🧑‍🍳',title:'Кухні',text:'HPL-фасади, стільниці compact, інтер’єрні панелі.'},
              {icon:'🗄️',title:'Шафи та гардеробні',text:'Купе, радіусні фасади, системи зберігання.'},
              {icon:'🧑‍💻',title:'Робочі місця',text:'Стільниці на металокаркасі, тумби, кабель-менеджмент.'},
              {icon:'🤝',title:'Переговорні та ресепшн',text:'Функціональні столи, акустичні рішення.'},
              {icon:'🛍️',title:'Торгівельні острівці',text:'Вітрини, дисплеї, підсвітка, POS.'},
              {icon:'🧒',title:'Дитячі',text:'Безпечні матеріали, заокруглені фаски, надійна фурнітура.'},
            ].map((p,i)=>(
              <Card key={i} data-aos="fade-up" data-aos-delay={i*50}>
                <div className="thumb">{p.icon}</div>
                <div className="body">
                  <h3>{p.title}</h3>
                  <p>{p.text}</p>
                </div>
              </Card>
            ))}
          </Grid>
        </Container>
      </Section>

      {/* ARTICLES PREVIEW */}
      <Section className="alt">
        <Container>
          <h2 data-aos="fade-up">Статті та корисні матеріали</h2>
          <Grid style={{marginTop:16}}>
            {articles.map((a,i)=>(
              <Card key={a.id} as="article" data-aos="fade-up" data-aos-delay={i*60}>
                <div className="thumb">📚</div>
                <div className="body">
                  <Pill>{new Date(a.date).toLocaleDateString('uk-UA')}</Pill>
                  <h3 style={{marginTop:8}}>{a.title}</h3>
                  <p>Короткий огляд теми та практичні поради від команди MOMV.</p>
                  <div style={{marginTop:10}}>
                    <Button href={`/articles/${a.id}`}>Читати</Button>
                  </div>
                </div>
              </Card>
            ))}
          </Grid>
        </Container>
      </Section>

      {/* CTA */}
      <Section>
        <Container>
          <CTA data-aos="zoom-in">
            <h2>Почнемо проєкт?</h2>
            <p>Надішліть план приміщення або орієнтовні габарити — підготуємо пропозицію з варіантами матеріалів і термінами.</p>
            <div style={{display:'flex', gap:10, flexWrap:'wrap'}}>
              <Button primary href="/contacts">Залишити запит</Button>
              <Button href="/hpl">Подивитись палітру HPL</Button>
            </div>
          </CTA>
        </Container>
      </Section>
    </Page>
  );
}
